/**
 * Makes a photo small enough for a menu before it leaves the device: at most 800 pixels on
 * its long side, as JPEG. A phone photo of 4 MB becomes about 80 KB, so menus load fast
 * even on a weak connection. Returns the bytes as base64, without the "data:" prefix.
 */
export async function shrinkPhoto(file: File, maxSide = 800, quality = 0.82): Promise<{ base64: string; url: string }> {
  if (!file.type.startsWith('image/')) throw new Error('not_image');
  const bitmap = await loadImage(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d')!;
  // Transparent PNGs get a white background instead of black.
  g.fillStyle = 'white';
  g.fillRect(0, 0, w, h);
  g.drawImage(bitmap, 0, 0, w, h);
  let url = canvas.toDataURL('image/jpeg', quality);
  // Very detailed photos: try once more, a little softer.
  if (url.length > 500_000) url = canvas.toDataURL('image/jpeg', 0.7);
  return { base64: url.slice(url.indexOf(',') + 1), url };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const src = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(src);
      reject(new Error('not_image'));
    };
    img.src = src;
  });
}
